/**
 * Controlo de notificações push do dispositivo (#124 PR-3), ao lado do
 * indicador de tempo real do Garçom (`waiter-realtime`) e da Cozinha
 * (`kitchen-realtime`). A permissão só é pedida DEPOIS do clique (nunca ao
 * abrir a página); os 4 estados da UX da #124:
 *
 *   default   → botão «Ativar notificações» + nota de escopo por baixo
 *   enabled   → badge «Notificações ativas» (zona aria-live="polite")
 *   denied    → badge + frase «bloqueadas no browser» (sem botão)
 *   needs_install (iOS sem a PWA) → badge «Instale a app» + passos (sem botão)
 *   unsupported → texto «browser não recebe notificações» (sem botão)
 *
 * Erro ao ativar → toast; o tempo real por WebSocket continua (critério 8).
 */
import { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../lib/i18n.js'
import { isImpersonationActive } from '../lib/impersonation.js'
import {
  currentPushSubscription,
  enablePush,
  pushSupport,
  PushPermissionDeniedError,
} from '../lib/push.js'
import type { PushChannel, PushSupport } from '../lib/push.js'
import { Badge, Button, toast } from './ui/index.js'
import { cn } from '../lib/utils.js'

export function PushToggle({ channel }: { channel: PushChannel }) {
  const { t } = useI18n()
  const [support, setSupport] = useState<PushSupport>(() => pushSupport())
  /** null = ainda a verificar a subscrição existente (evita piscar o botão). */
  const [subscribed, setSubscribed] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    currentPushSubscription()
      .then((subscription) => {
        if (live) setSubscribed(!!subscription)
      })
      .catch(() => {
        if (live) setSubscribed(false)
      })
    return () => {
      live = false
    }
  }, [])

  const onEnable = useCallback(async () => {
    setBusy(true)
    try {
      await enablePush(channel)
      setSubscribed(true)
      setSupport(pushSupport())
    } catch (err) {
      // permissão recusada vira estado «bloqueadas»; o resto é toast
      setSupport(pushSupport())
      setSubscribed(false)
      if (!(err instanceof PushPermissionDeniedError)) toast.error(t('admin.push.error'))
    } finally {
      setBusy(false)
    }
  }, [channel, t])

  // badge/texto longo dentro de pílula: quebra em vez de estourar a barra
  const badgeText = cn('max-w-[280px] whitespace-normal text-left leading-snug')

  let control: React.ReactNode
  if (subscribed) {
    // depois de ativar: badge numa zona aria-live="polite" (UX da #124)
    control = (
      <Badge color="green" className="min-h-[28px]">
        <span aria-hidden="true">✓ </span>
        {t('admin.push.enabled')}
      </Badge>
    )
  } else if (support === 'needs_install') {
    control = (
      <>
        <Badge color="gray" className={badgeText}>
          {t('admin.push.unsupported_install')}
        </Badge>
        <span className="max-w-[280px] text-xs text-gray-500">
          {t('admin.push.install_ios_steps')}
        </span>
      </>
    )
  } else if (support === 'denied') {
    control = (
      <Badge color="yellow" className={badgeText}>
        {t('admin.push.blocked')}
      </Badge>
    )
  } else if (support === 'unsupported') {
    control = (
      <span className="max-w-[280px] text-xs text-gray-500">{t('admin.push.unsupported')}</span>
    )
  } else {
    // default (ou granted sem subscrição — ex.: chaves VAPID rodadas)
    control = (
      <>
        <Button
          size="sm"
          variant="secondary"
          loading={busy}
          aria-busy={busy}
          className="min-h-[44px]"
          data-testid="push-enable"
          onClick={() => void onEnable()}
        >
          {t('admin.push.enable')}
        </Button>
        <span className="max-w-[280px] text-xs text-gray-500">{t('admin.push.scope_note')}</span>
      </>
    )
  }

  // decisão do PO #124: sessão da plataforma (impersonação) não ativa
  // notificações — não é equipe do restaurante; a API recusaria com 403.
  // DEPOIS dos hooks (regras do React); o estado de baixo não muda nada.
  if (isImpersonationActive()) return null

  return (
    <div
      data-testid="push-toggle"
      data-state={subscribed ? 'enabled' : support}
      data-channel={channel}
      aria-live="polite"
      className="flex flex-col items-start gap-1"
    >
      {control}
    </div>
  )
}
